import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/joy";
import HistoryIcon from "@mui/icons-material/History";
import { theme } from "styles/theme";
import { type ChatThreadDTO, listThreads } from "./chatHistoryApi";
import { taskEditorScrollbarSx } from "../utils/taskEditorScrollbarSx";

interface PreviousThreadsDropdownProps {
  taskId: string;
  currentThreadId: string | null;
  onSelectThread: (threadId: string) => void;
}

// 우측 헤더의 "대화 내역" 버튼 = 이 dropdown 의 트리거.
// 클릭 시 같은 task 의 모든 thread (cleared 포함) 펼침. v1 은 단순 dropdown — search/페이지네이션 없음.
// thread 1개일 때도 열려서 현재 thread 정보를 selected 상태로 보여준다.

// BE 가 timezone-naive ISO ("2026-05-27T01:30:00") 를 보내면 JS Date 가 로컬타임으로 해석되어
// KST 환경에서 9시간 미래로 잘못 파싱됨 → diff 음수 → 항상 "방금" 표시 버그.
// marker (Z 또는 ±HH:MM) 없으면 UTC 로 간주해 강제로 Z 붙임.
function parseIsoMs(iso: string): number {
  const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso);
  return new Date(hasTz ? iso : iso + "Z").getTime();
}

function formatRelative(iso: string): string {
  const then = parseIsoMs(iso);
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "방금";
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}일 전`;
  return new Date(then).toLocaleDateString("ko-KR");
}

export function PreviousThreadsDropdown({
  taskId,
  currentThreadId,
  onSelectThread,
}: PreviousThreadsDropdownProps) {
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState<ChatThreadDTO[]>([]);
  const [hasAny, setHasAny] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // 열 때마다 새로 조회 — 캐싱은 부담스럽지 않은 호출 비용.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void listThreads(taskId, { includeCleared: true, limit: 20 }).then(
      (rows) => {
        if (!cancelled) setThreads(rows);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [open, taskId]);

  // 트리거 가시성용 count probe. currentThreadId 가 null 이어도 cleared thread 가 있으면
  // 버튼은 유지되어야 함 — "새로운 대화" 직후 다음 send 전까지 버튼이 사라지는 깜빡임 방지.
  // taskId 만 dependency: currentThreadId 변경(예: switchThread) 시는 갱신 불필요 (cleared count
  // 는 변하지 않음). 새 thread 가 처음 만들어질 때(threadId null→값)에만 추가로 갱신.
  useEffect(() => {
    setHasAny(false);
  }, [taskId]);

  useEffect(() => {
    let cancelled = false;
    void listThreads(taskId, { includeCleared: true, limit: 1 }).then(
      (rows) => {
        if (!cancelled && rows.length > 0) setHasAny(true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [taskId, currentThreadId]);

  // 바깥 영역 클릭 시 닫힘. 트리거는 ref 안에 있으므로 자체 toggle 과 충돌 없음.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  // 대화 내역이 없으면(현재 thread 도, cleared thread 도 없음) 숨기지 않고 비활성화.
  const disabled = !currentThreadId && !hasAny;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "28px",
      }}
    >
      <Box
        component="button"
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((p) => !p);
        }}
        aria-label="대화 내역"
        title="대화 내역"
        sx={{
          background: "none",
          border: "none",
          padding: 0,
          margin: 0,
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.4 : 1,
          display: "inline-flex",
          alignItems: "center",
          lineHeight: 0,
          color: theme.vars.palette.Content.Weak,
          "&:hover": {
            color: disabled
              ? theme.vars.palette.Content.Weak
              : theme.vars.palette.Content.Medium,
          },
        }}
      >
        <HistoryIcon sx={{ fontSize: "18px" }} />
      </Box>
      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "32px",
            right: 0,
            minWidth: 360,
            maxWidth: 480,
            maxHeight: 360,
            overflowY: "auto",
            backgroundColor: theme.vars.palette.Surface.Forward,
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            borderRadius: "8px",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            zIndex: 10,
            ...taskEditorScrollbarSx,
          }}
        >
          {threads.length === 0 && (
            <Box
              sx={{
                p: "12px",
                fontSize: "12px",
                color: theme.vars.palette.Content.Weak,
              }}
            >
              불러오는 중…
            </Box>
          )}
          {threads.map((t) => {
            const isCurrent = t.thread_id === currentThreadId;
            return (
              <Box
                key={t.thread_id}
                title={t.thread_id}
                onClick={() => {
                  if (!isCurrent) onSelectThread(t.thread_id);
                  setOpen(false);
                }}
                sx={{
                  px: "12px",
                  py: "10px",
                  cursor: isCurrent ? "default" : "pointer",
                  borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
                  "&:last-child": { borderBottom: "none" },
                  backgroundColor: isCurrent
                    ? theme.vars.palette.Background.Level2
                    : "transparent",
                  "&:hover": {
                    backgroundColor: isCurrent
                      ? theme.vars.palette.Background.Level2
                      : theme.vars.palette.Background.Level1,
                  },
                }}
              >
                <Typography
                  level="body-xs"
                  sx={{
                    fontSize: "13px",
                    lineHeight: 1.4,
                    fontWeight: 500,
                    color: theme.vars.palette.Content.Strong,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {t.last_user_message || "(메시지 없음)"}
                  {t.is_cleared && (
                    <Box
                      component="span"
                      sx={{
                        ml: "6px",
                        fontSize: "11px",
                        color: theme.vars.palette.Content.Weak,
                        fontWeight: 500,
                      }}
                    >
                      · 종료됨
                    </Box>
                  )}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    mt: "4px",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      fontSize: "10px",
                      color: theme.vars.palette.Content.Weak,
                      flexShrink: 0,
                    }}
                  >
                    #{t.thread_id.slice(0, 6)}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "10px",
                      color: theme.vars.palette.Content.Weak,
                      flexShrink: 0,
                    }}
                  >
                    · {formatRelative(t.updated_at)}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
